import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { threatChecklistCoverage } from '../EventsTab/categoryChecklistMatch';
import type { ResourceRow } from './ResourceTabCard';

export function ThreatChecklistCoverageRing({
  threats,
  threat_checklists,
  size = 18,
}: {
  threats: ResourceRow['threats'];
  threat_checklists: ResourceRow['threat_checklists'];
  size?: number;
}) {
  const { covered, total, percent } = threatChecklistCoverage({
    threats,
    threat_checklists,
  });
  const clamped = Math.max(0, Math.min(100, percent));
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clamped / 100) * circumference;
  const label =
    total === 0
      ? 'No automation rules'
      : `${covered} of ${total} automation rules have a response checklist`;
  const completeRing = total > 0 && clamped >= 100;

  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              className="tw:relative tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring"
              style={{ width: size, height: size }}
              aria-label={label}
              onClick={(event) => event.stopPropagation()}
            >
              <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="tw:-rotate-90"
                aria-hidden="true"
              >
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={strokeWidth}
                  className="tw:text-muted-foreground/30"
                />
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className={cn(
                    'tw:transition-[stroke-dashoffset]',
                    completeRing ? 'tw:text-emerald-400' : 'tw:text-primary'
                  )}
                />
              </svg>
              <span
                className={cn(
                  'tw:absolute tw:text-[7px] tw:font-semibold tw:tabular-nums tw:leading-none',
                  completeRing
                    ? 'tw:text-emerald-700 tw:dark:text-emerald-200'
                    : 'tw:text-foreground'
                )}
              >
                {clamped}
              </span>
            </span>
          }
        />
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
